import React, { useState, useEffect, useContext } from 'react';
import api, { resolveScopedRestaurantId } from '../../utils/api';
import { AuthContext } from '../../context/AuthContext';
import ImageUpload from '../../components/Admin/ImageUpload';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  FunnelIcon,
  XMarkIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import toast from '../../utils/notifications';

const MenuManagement = () => {
  const { user } = useContext(AuthContext);
  const [menuItems, setMenuItems] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Stats from API
  const [stats, setStats] = useState({
    total_items: 0,
    available_items: 0,
    sold_out_items: 0,
    unavailable_items: 0
  });
  
  // Add Item Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '',
    price: '',
    category: '',
    restaurant_id: '',
    image_url: null
  });
  const [addingItem, setAddingItem] = useState(false);
  
  // Edit Item Modal State
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    price: '',
    category: '',
    description: '',
    status: 'available'
  });
  const [updatingItem, setUpdatingItem] = useState(false);

  // Delete Confirmation Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deletingItem, setDeletingItem] = useState(false);

  const getMenuItemId = (item) => {
    const parsedId = Number.parseInt(item?.menu_item_id ?? item?.id ?? 0, 10);
    return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : 0;
  };

  const normalizeMenuItem = (item) => {
    if (!item || typeof item !== 'object') {
      return item;
    }

    const resolvedId = getMenuItemId(item);
    return {
      ...item,
      menu_item_id: resolvedId || item.menu_item_id || item.id || null,
    };
  };

  const normalizeMenuItems = (items) => (Array.isArray(items) ? items.map(normalizeMenuItem) : []);

  const fetchRestaurants = async () => {
    try {
      const response = await api.get('/restaurants');
      setRestaurants(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch restaurants');
    }
  };

  const fetchStats = async () => {
    try {
      const params = {};
      if (user?.role === 'staff' && user?.restaurant_id) {
        params.restaurant_id = user.restaurant_id;
      } else if (selectedRestaurant && selectedRestaurant.trim()) {
        params.restaurant_id = selectedRestaurant.trim();
      } else {
        const scopedRestaurantId = resolveScopedRestaurantId(user);
        if (scopedRestaurantId) {
          params.restaurant_id = scopedRestaurantId;
        }
      }

      const response = await api.get('/menu-items/stats', { params });
      if (response.data.data) {
        setStats(response.data.data);
      }
    } catch (error) {
      // Fallback: calculate from current menuItems
      updateStatsFromItems();
    }
  };

  const updateStatsFromItems = () => {
    setStats({
      total_items: menuItems.length,
      available_items: menuItems.filter(item => item.status === 'available').length,
      sold_out_items: menuItems.filter(item => item.status === 'sold_out').length,
      unavailable_items: menuItems.filter(item => item.status === 'unavailable').length
    });
  };

  // Initial load - fetch restaurants and items once
  useEffect(() => {
    const loadInitialData = async () => {
      await fetchRestaurants();
      await fetchMenuItemsAndCategories();
      // Only fetch stats if user is authenticated
      if (user) {
        await fetchStats();
      }
    };
    loadInitialData();
  }, [user]);

  // Update stats whenever menuItems change
  useEffect(() => {
    if (menuItems.length > 0) {
      updateStatsFromItems();
    }
  }, [menuItems]);

  // Refresh API stats whenever restaurant context changes.
  useEffect(() => {
    if (user) {
      fetchStats();
    }
  }, [user, selectedRestaurant]);

  // Set default restaurant based on user role (only run when user changes)
  useEffect(() => {
    if (user) {
      if (user.role === 'staff' && user.restaurant_id) {
        setSelectedRestaurant(user.restaurant_id.toString());
        localStorage.setItem('selected_restaurant_id', user.restaurant_id.toString());
      } else if (user.role === 'admin') {
        const scopedRestaurantId = resolveScopedRestaurantId(user);
        const savedRestaurantId = localStorage.getItem('selected_restaurant_id');
        const initialRestaurantId = savedRestaurantId || (scopedRestaurantId ? scopedRestaurantId.toString() : '');
        setSelectedRestaurant(initialRestaurantId);
        if (initialRestaurantId) {
          localStorage.setItem('selected_restaurant_id', initialRestaurantId);
        }
      }
    }
  }, [user]);

  useEffect(() => {
    if (selectedRestaurant && selectedRestaurant.trim()) {
      localStorage.setItem('selected_restaurant_id', selectedRestaurant.trim());
      return;
    }

    if (user?.role === 'admin') {
      localStorage.removeItem('selected_restaurant_id');
    }
  }, [selectedRestaurant, user]);

  // Fetch items when filters change (but not on initial mount)
  useEffect(() => {
    // Skip if this is initial render (items will be loaded by initial useEffect)
    if (menuItems.length > 0 || selectedRestaurant !== '') {
      fetchMenuItems('', selectedCategory, availabilityFilter);
    }
  }, [selectedCategory, availabilityFilter, selectedRestaurant]);

  const fetchMenuItemsAndCategories = async () => {
    try {
      setLoading(true);
      
      // Build params with restaurant filtering for staff
      const params = { per_page: 10000 };
      const scopedRestaurantId = resolveScopedRestaurantId(user);
      
      // Staff users: automatically filter by their restaurant
      if (user?.role === 'staff' && user?.restaurant_id) {
        params.restaurant_id = user.restaurant_id;
      }
      // Admin: use scoped or saved selection if present
      else if (scopedRestaurantId) {
        params.restaurant_id = scopedRestaurantId;
      }

      const response = await api.get('/menu-items', { params });
      const items = normalizeMenuItems(response.data.data.data || response.data.data || []);
      
      // Extract unique categories from items
      const uniqueCategories = [...new Set(items.map(item => item.category).filter(Boolean))];
      setCategories(uniqueCategories.sort());
      
      // Set initial items
      setMenuItems(Array.isArray(items) ? items : []);
    } catch (error) {
      toast.error('Failed to fetch menu items');
      setMenuItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMenuItems = async (search = '', category = '', filter = 'all') => {
    try {
      setLoading(true);
      const params = { per_page: 10000 };
      
      // Use 'q' parameter for search (as per backend API)
      if (search && search.trim()) {
        params.q = search.trim();
      }
      
      // Use 'category' parameter for category filter
      if (category && category.trim()) {
        params.category = category.trim();
      }

      // Restaurant filtering logic
      // Staff: Always filter by their restaurant (override any selectedRestaurant)
      if (user?.role === 'staff' && user?.restaurant_id) {
        params.restaurant_id = user.restaurant_id;
      }
      // Admin: Use selectedRestaurant filter if set
      else if (selectedRestaurant && selectedRestaurant.trim()) {
        params.restaurant_id = selectedRestaurant.trim();
      }
      // Fallback to scoped helper so admins following links with ?restaurant_id get the right view
      else {
        const scopedRestaurantId = resolveScopedRestaurantId(user);
        if (scopedRestaurantId) {
          params.restaurant_id = scopedRestaurantId;
        }
      }

      const response = await api.get('/menu-items', { params });

      let items = normalizeMenuItems(response.data.data.data || response.data.data || []);
      
      // Apply status filter on frontend since backend doesn't support it
      if (filter !== 'all') {
        items = items.filter(item => item.status === filter);
      }
      
      setMenuItems(items);
    } catch (error) {
      toast.error('Failed to fetch menu items');
      setMenuItems([]);
    } finally {
      setLoading(false);
    }
  };

  const updateItemStatus = async (menuItemId, newStatus) => {
    const resolvedMenuItemId = Number.parseInt(menuItemId, 10);
    if (!Number.isInteger(resolvedMenuItemId) || resolvedMenuItemId <= 0) {
      toast.error('Invalid menu item selected. Please refresh and try again.');
      return;
    }

    try {
      // Use standard PUT endpoint to update menu item
      await api.put(`/menu-items/${resolvedMenuItemId}`, {
        status: newStatus
      });
      
      // Update local state
      setMenuItems(menuItems.map(item =>
        getMenuItemId(item) === resolvedMenuItemId
          ? { ...item, status: newStatus }
          : item
      ));
      
      // Refresh stats after status change
      fetchStats();
      
      toast.success(`Menu item marked as ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update menu item status');
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    
    if (!newItem.name.trim() || !newItem.price || !newItem.category.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Determine restaurant ID based on user role
    let restaurantId;
    if (user?.role === 'staff' && user?.restaurant_id) {
      // Staff: Always use their restaurant_id
      restaurantId = user.restaurant_id.toString();
    } else if (user?.role === 'admin') {
      // Admin: Use selected restaurant from form
      restaurantId = newItem.restaurant_id;
    }
    
    if (!restaurantId) {
      toast.error('Please select a restaurant');
      return;
    }

    if (parseFloat(newItem.price) <= 0) {
      toast.error('Price must be greater than 0');
      return;
    }

    setAddingItem(true);
    try {
      const response = await api.post('/menu-items/quick', {
        name: newItem.name.trim(),
        price: parseFloat(newItem.price),
        category: newItem.category.trim(),
        restaurant_id: parseInt(restaurantId),
        image_url: newItem.image_url
      });

      // Add new item to the list with normalized ID shape
      const createdItem = normalizeMenuItem(response.data.data || {});
      const createdItemId = getMenuItemId(createdItem);
      if (createdItemId > 0) {
        setMenuItems((prevItems) => [
          createdItem,
          ...prevItems.filter((item) => getMenuItemId(item) !== createdItemId),
        ]);
      } else {
        await fetchMenuItems(searchTerm, selectedCategory, availabilityFilter);
      }
      
      // Update categories if new category added
      if (!categories.includes(newItem.category.trim())) {
        setCategories([...categories, newItem.category.trim()].sort());
      }

      // Refresh stats after adding item
      fetchStats();

      toast.success('Menu item added successfully');
      setShowAddModal(false);
      setNewItem({ name: '', price: '', category: '', restaurant_id: '', image_url: null });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add menu item');
    } finally {
      setAddingItem(false);
    }
  };

  const openEditItemModal = (item) => {
    const normalizedItem = normalizeMenuItem(item);
    setEditingItem(normalizedItem);
    setEditForm({
      name: normalizedItem.name || '',
      price: normalizedItem.price != null ? normalizedItem.price.toString() : '',
      category: normalizedItem.category || '',
      description: normalizedItem.description || '',
      status: normalizedItem.status || 'available'
    });
    setShowEditItemModal(true);
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();

    if (!editForm.name.trim() || !editForm.category.trim()) {
      toast.error('Name and category are required');
      return;
    }

    if (!editForm.price || parseFloat(editForm.price) <= 0) {
      toast.error('Please enter a valid price greater than 0');
      return;
    }

    const itemId = getMenuItemId(editingItem);
    if (!itemId) {
      toast.error('Invalid menu item selected. Please refresh and try again.');
      return;
    }

    setUpdatingItem(true);
    try {
      const payload = {
        name: editForm.name.trim(),
        price: parseFloat(editForm.price),
        category: editForm.category.trim(),
        description: editForm.description.trim() || null,
        status: editForm.status
      };

      await api.put(`/menu-items/${itemId}`, payload);

      if (payload.category && !categories.includes(payload.category)) {
        setCategories((prev) => [...prev, payload.category].sort());
      }

      // Update local state so the table/card reflects edits immediately.
      setMenuItems(menuItems.map(item =>
        getMenuItemId(item) === getMenuItemId(editingItem)
          ? {
              ...item,
              name: payload.name,
              price: payload.price,
              category: payload.category,
              description: payload.description,
              status: payload.status
            }
          : item
      ));

      fetchStats();

      toast.success('Menu item updated successfully');
      setShowEditItemModal(false);
      setEditingItem(null);
      setEditForm({
        name: '',
        price: '',
        category: '',
        description: '',
        status: 'available'
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update menu item');
    } finally {
      setUpdatingItem(false);
    }
  };

  const openDeleteModal = (item) => {
    setItemToDelete(normalizeMenuItem(item));
    setShowDeleteModal(true);
  };

  const handleDeleteItem = async () => {
    if (!itemToDelete) return;

    const itemId = getMenuItemId(itemToDelete);
    if (!itemId) {
      toast.error('Unable to delete this item. Please refresh and try again.');
      return;
    }

    setDeletingItem(true);
    try {
      await api.delete(`/menu-items/${itemId}`);

      // Update local state
      setMenuItems(menuItems.filter(item => getMenuItemId(item) !== itemId));

      // Refresh stats after deletion
      fetchStats();

      toast.success('Menu item deleted successfully');
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete item');
    } finally {
      setDeletingItem(false);
    }
  };

  const handleImageChange = async (menuItemId, imageUrl) => {
    const resolvedMenuItemId = Number.parseInt(menuItemId, 10);
    if (!Number.isInteger(resolvedMenuItemId) || resolvedMenuItemId <= 0) {
      toast.error('Invalid menu item selected. Please refresh and try again.');
      return;
    }

    try {
      // Update the menu item with new image URL
      await api.put(`/menu-items/${resolvedMenuItemId}`, {
        image_url: imageUrl
      });

      // Update local state
      setMenuItems(menuItems.map(item =>
        getMenuItemId(item) === resolvedMenuItemId
          ? { ...item, image_url: imageUrl }
          : item
      ));

      toast.success(imageUrl ? 'Image uploaded successfully' : 'Image removed successfully');
    } catch (error) {
      toast.error('Failed to update menu item image');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Fetch only when Search button is clicked
    fetchMenuItems(searchTerm, selectedCategory, availabilityFilter);
  };

  const handleRestaurantChange = (e) => {
    setSelectedRestaurant(e.target.value);
  };

  const getStatusBadge = (status) => {
    if (status === 'available') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="w-4 h-4 mr-1" />
          Available
        </span>
      );
    } else if (status === 'sold_out') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircleIcon className="w-4 h-4 mr-1" />
          Sold Out
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        <XCircleIcon className="w-4 h-4 mr-1" />
        Unavailable
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Menu Management</h2>
            <p className="mt-1 text-xs sm:text-sm text-gray-600">
              Manage menu items and mark items as available or sold out
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors shadow-sm text-sm font-medium"
          >
            <PlusIcon className="h-5 w-5" />
            Add Menu Item
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow-sm rounded-lg p-3 sm:p-6">
        <form onSubmit={handleSearch} className="space-y-3 sm:space-y-4">
          {/* Search Bar */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search menu items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 text-sm"
                />
              </div>
            </div>
            {user?.role === 'admin' && (
              <div className="sm:w-64">
                <select
                  value={selectedRestaurant}
                  onChange={handleRestaurantChange}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 text-sm"
                >
                  <option value="">All Restaurants</option>
                  {restaurants.map((restaurant) => (
                    <option key={restaurant.restaurant_id} value={restaurant.restaurant_id}>
                      {restaurant.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <button
              type="submit"
              className="px-3 sm:px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors text-sm whitespace-nowrap"
            >
              Search
            </button>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 sm:px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <FunnelIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="hidden sm:inline">Filters</span>
            </button>
          </div>

          {/* Filter Options */}
          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pt-4 border-t">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 text-sm"
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Status Filter
                </label>
                <select
                  value={availabilityFilter}
                  onChange={(e) => setAvailabilityFilter(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 text-sm"
                >
                  <option value="all">All Items</option>
                  <option value="available">Available Only</option>
                  <option value="sold_out">Sold Out Only</option>
                  <option value="unavailable">Unavailable Only</option>
                </select>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4">
        <div className="bg-white shadow-sm rounded-lg p-3 sm:p-5">
          <div className="text-[10px] sm:text-sm font-medium text-gray-500 truncate">Total Items</div>
          <div className="mt-1.5 sm:mt-2 text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">{stats.total_items}</div>
        </div>
        <div className="bg-white shadow-sm rounded-lg p-3 sm:p-5">
          <div className="text-[10px] sm:text-sm font-medium text-gray-500 truncate">Available</div>
          <div className="mt-1.5 sm:mt-2 text-xl sm:text-2xl lg:text-3xl font-bold text-green-600">
            {stats.available_items}
          </div>
        </div>
        <div className="bg-white shadow-sm rounded-lg p-3 sm:p-5">
          <div className="text-[10px] sm:text-sm font-medium text-gray-500 truncate">Sold Out</div>
          <div className="mt-1.5 sm:mt-2 text-xl sm:text-2xl lg:text-3xl font-bold text-red-600">
            {stats.sold_out_items}
          </div>
        </div>
        <div className="bg-white shadow-sm rounded-lg p-3 sm:p-5">
          <div className="text-[10px] sm:text-sm font-medium text-gray-500 truncate">Unavailable</div>
          <div className="mt-1.5 sm:mt-2 text-xl sm:text-2xl lg:text-3xl font-bold text-gray-600">
            {stats.unavailable_items}
          </div>
        </div>
      </div>

      {/* Menu Items Table/Cards */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Item Name
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                {/* Show Restaurant column only for admin viewing all restaurants */}
                {user?.role === 'admin' && !selectedRestaurant && (
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Restaurant
                  </th>
                )}
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Image
                </th>
                <th className="hidden lg:table-cell px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {menuItems.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 lg:px-6 py-12 text-center text-sm text-gray-500">
                    No menu items found
                  </td>
                </tr>
              ) : (
                menuItems.map((item, index) => (
                  <tr key={getMenuItemId(item) || `${item.name}-${index}`} className="hover:bg-gray-50">
                    <td className="px-4 lg:px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{item.name}</div>
                      <div className="text-xs text-gray-500 line-clamp-1">{item.description}</div>
                    </td>
                    <td className="px-4 lg:px-6 py-4">
                      <span className="text-sm text-gray-900">{item.category}</span>
                    </td>
                    {/* Show Restaurant column only for admin viewing all restaurants */}
                    {user?.role === 'admin' && !selectedRestaurant && (
                      <td className="px-4 lg:px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {item.restaurant?.name || `ID: ${item.restaurant_id}`}
                        </span>
                      </td>
                    )}
                    <td className="px-4 lg:px-6 py-4">
                      <span className="text-sm font-medium text-gray-900">
                        ₱{parseFloat(item.price).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 lg:px-6 py-4">
                      <div className="max-w-xs">
                        <ImageUpload
                          currentImageUrl={item.image_url}
                          onImageChange={(imageUrl) => handleImageChange(getMenuItemId(item), imageUrl)}
                        />
                      </div>
                    </td>
                    <td className="hidden lg:table-cell px-4 lg:px-6 py-4">
                      {getStatusBadge(item.status || 'available')}
                    </td>
                    <td className="px-4 lg:px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditItemModal(item)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 rounded-md hover:bg-amber-100 transition-colors"
                          title="Edit Item"
                        >
                          <PencilIcon className="h-3.5 w-3.5" />
                          Edit
                        </button>
                        <button
                          onClick={() => openDeleteModal(item)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
                          title="Delete Item"
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                          Delete
                        </button>
                        <select
                          value={item.status || 'available'}
                          onChange={(e) => updateItemStatus(getMenuItemId(item), e.target.value)}
                          className="flex-1 min-w-[100px] px-2 py-1.5 rounded border border-gray-300 bg-white text-xs font-medium text-gray-700 hover:border-gray-400 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors"
                        >
                          <option value="available">✓ Available</option>
                          <option value="sold_out">✗ Sold Out</option>
                          <option value="unavailable">⊘ Unavailable</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile/Tablet Card View */}
        <div className="lg:hidden divide-y divide-gray-200">
          {menuItems.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-gray-500">
              No menu items found
            </div>
          ) : (
            menuItems.map((item, index) => (
              <div key={getMenuItemId(item) || `${item.name}-${index}`} className="p-4 hover:bg-gray-50">
                <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-4">
                  <div className="space-y-3">
                    {/* Header with name and status */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-900">{item.name}</h3>
                        {item.description && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.description}</p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {getStatusBadge(item.status || 'available')}
                      </div>
                    </div>
                    
                    {/* Category and Price */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        {item.category}
                      </span>
                      {/* Show Restaurant badge for admin viewing all restaurants */}
                      {user?.role === 'admin' && !selectedRestaurant && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          {item.restaurant?.name || `ID: ${item.restaurant_id}`}
                        </span>
                      )}
                      <span className="text-sm font-semibold text-gray-900">
                        ₱{parseFloat(item.price).toFixed(2)}
                      </span>
                    </div>

                    {/* Image Management */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        Menu Item Image:
                      </label>
                      <ImageUpload
                        currentImageUrl={item.image_url}
                        onImageChange={(imageUrl) => handleImageChange(getMenuItemId(item), imageUrl)}
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => openEditItemModal(item)}
                        className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 rounded-md hover:bg-amber-100 transition-colors"
                      >
                        <PencilIcon className="h-4 w-4" />
                        Edit Item
                      </button>
                      <button
                        onClick={() => openDeleteModal(item)}
                        className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
                      >
                        <TrashIcon className="h-4 w-4" />
                        Delete
                      </button>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Update Status:
                      </label>
                      <select
                        value={item.status || 'available'}
                        onChange={(e) => updateItemStatus(getMenuItemId(item), e.target.value)}
                        className="w-full px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:border-gray-400 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors shadow-sm"
                      >
                        <option value="available">✓ Available</option>
                        <option value="sold_out">✗ Sold Out</option>
                        <option value="unavailable">⊘ Unavailable</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add Item Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => !addingItem && setShowAddModal(false)}
            ></div>

            {/* Modal panel */}
            <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Add New Menu Item</h3>
                <button
                  onClick={() => !addingItem && setShowAddModal(false)}
                  disabled={addingItem}
                  className="text-gray-400 hover:text-gray-500 transition-colors disabled:opacity-50"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleAddItem} className="space-y-4">
                {/* Restaurant Selector - Only show for admin */}
                {user?.role === 'admin' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Restaurant <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={newItem.restaurant_id}
                      onChange={(e) => setNewItem({ ...newItem, restaurant_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                      disabled={addingItem}
                      required
                    >
                      <option value="">Select Restaurant</option>
                      {restaurants.map((restaurant) => (
                        <option key={restaurant.restaurant_id} value={restaurant.restaurant_id}>
                          {restaurant.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Item Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newItem.name}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                    placeholder="e.g., Fried Chicken"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    disabled={addingItem}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price (₱) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newItem.price}
                    onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    disabled={addingItem}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    list="categories-list"
                    value={newItem.category}
                    onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                    placeholder="e.g., Main Course, Beverages"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    disabled={addingItem}
                    required
                  />
                  <datalist id="categories-list">
                    {categories.map((cat) => (
                      <option key={cat} value={cat} />
                    ))}
                  </datalist>
                  <p className="mt-1 text-xs text-gray-500">
                    Select existing or type a new category
                  </p>
                </div>

                {/* Image Upload Component */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Menu Item Image
                  </label>
                  <ImageUpload
                    currentImageUrl={newItem.image_url}
                    onImageChange={(imageUrl) => setNewItem({ ...newItem, image_url: imageUrl })}
                    disabled={addingItem}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    disabled={addingItem}
                    className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addingItem}
                    className="flex-1 px-4 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {addingItem ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Adding...
                      </>
                    ) : (
                      <>
                        <PlusIcon className="h-4 w-4" />
                        Add Item
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Item Modal */}
      {showEditItemModal && editingItem && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => !updatingItem && setShowEditItemModal(false)}
            ></div>

            {/* Modal panel */}
            <div className="inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Edit Menu Item</h3>
                <button
                  onClick={() => !updatingItem && setShowEditItemModal(false)}
                  disabled={updatingItem}
                  className="text-gray-400 hover:text-gray-500 transition-colors disabled:opacity-50"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs uppercase tracking-wide text-gray-500">Editing Item</p>
                <p className="text-base font-semibold text-gray-900">{editingItem.name}</p>
                <p className="text-xs text-gray-500 mt-1">
                  ID: {getMenuItemId(editingItem)}
                </p>
              </div>

              <form onSubmit={handleUpdateItem} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Item Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    disabled={updatingItem}
                    required
                    autoFocus
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      list="edit-categories-list"
                      value={editForm.category}
                      onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                      disabled={updatingItem}
                      required
                    />
                    <datalist id="edit-categories-list">
                      {categories.map((cat) => (
                        <option key={cat} value={cat} />
                      ))}
                    </datalist>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Price (₱) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editForm.price}
                      onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                      placeholder="0.00"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                      disabled={updatingItem}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    rows={3}
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    placeholder="Optional item description"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    disabled={updatingItem}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    disabled={updatingItem}
                  >
                    <option value="available">Available</option>
                    <option value="sold_out">Sold Out</option>
                    <option value="unavailable">Unavailable</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditItemModal(false)}
                    disabled={updatingItem}
                    className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updatingItem}
                    className="flex-1 px-4 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {updatingItem ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Updating...
                      </>
                    ) : (
                      <>
                        <PencilIcon className="h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && itemToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => !deletingItem && setShowDeleteModal(false)}
            ></div>

            {/* Modal panel */}
            <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Delete Menu Item</h3>
                <button
                  onClick={() => !deletingItem && setShowDeleteModal(false)}
                  disabled={deletingItem}
                  className="text-gray-400 hover:text-gray-500 transition-colors disabled:opacity-50"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">You are about to delete:</p>
                <p className="text-base font-semibold text-gray-900">{itemToDelete.name}</p>
                <p className="text-xs text-gray-500 mt-1">Category: {itemToDelete.category}</p>
                <p className="text-xs text-gray-500">Price: ₱{parseFloat(itemToDelete.price).toFixed(2)}</p>
              </div>

              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 font-medium">⚠️ Warning</p>
                <p className="text-xs text-yellow-700 mt-1">
                  This action cannot be undone. The menu item will be permanently deleted from the database.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deletingItem}
                  className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteItem}
                  disabled={deletingItem}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deletingItem ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <TrashIcon className="h-4 w-4" />
                      Delete Item
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuManagement;
